import type { ApiResponse } from "@/types/api";

export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  status: number;
  errors: string[];
  /** Optional structured error detail from the ApiResponse envelope
   *  (`details`). Used by callers that render field-tagged errors (e.g. the
   *  deal-import jump-to-field UX); undefined for the common case. */
  details?: unknown;
  constructor(message: string, status: number, errors: string[], details?: unknown) {
    super(message);
    this.status = status;
    this.errors = errors;
    this.details = details;
  }
}

/**
 * Single funnel that turns ANY caught error into a
 * non-empty user-facing string. Handles ApiError (the wrapper for the
 * ApiResponse envelope), ASP.NET ValidationProblemDetails (in case a model-
 * binding 400 slips past the envelope), plain Error instances, raw strings,
 * and anything else. The empty-toast UAT defect happened because callers
 * read `.errors.join("; ")` without filtering blanks; the array could
 * contain only an empty `response.statusText` on HTTP/2 (which carries no
 * status text), producing a red toast with no body. Every error path that
 * surfaces a message to the user MUST route through this helper.
 */
const FALLBACK_MESSAGE = "Something went wrong. Please try again.";

function isNonEmpty(s: unknown): s is string {
  return typeof s === "string" && s.trim().length > 0;
}

function flattenValidationErrors(errors: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [field, messages] of Object.entries(errors)) {
    const arr = Array.isArray(messages) ? messages : [messages];
    const filtered = arr.filter(isNonEmpty);
    if (filtered.length === 0) continue;
    // ASP.NET binds error keys as "$.Field" when the model binder fails on
    // a top-level property; strip the prefix for a cleaner display.
    const cleanField = field.replace(/^\$\.?/, "").trim();
    parts.push(cleanField ? `${cleanField}: ${filtered.join("; ")}` : filtered.join("; "));
  }
  return parts.join(" • ");
}

export function extractErrorMessage(err: unknown): string {
  if (err == null) return FALLBACK_MESSAGE;
  if (typeof err === "string") return isNonEmpty(err) ? err : FALLBACK_MESSAGE;

  if (err instanceof ApiError) {
    const joined = err.errors.filter(isNonEmpty).join("; ");
    if (isNonEmpty(joined)) return joined;
    if (isNonEmpty(err.message)) return err.message;
    return FALLBACK_MESSAGE;
  }

  if (err instanceof Error) {
    return isNonEmpty(err.message) ? err.message : `${err.name || "Error"} (no message)`;
  }

  if (typeof err === "object") {
    const obj = err as Record<string, unknown>;

    // Axios-style: { response: { data: <body> } }
    const response = obj.response as { data?: unknown } | undefined;
    if (response && "data" in response) {
      const fromBody = extractErrorMessage(response.data);
      if (fromBody !== FALLBACK_MESSAGE) return fromBody;
    }

    // ASP.NET ValidationProblemDetails: { errors: { Field: ["msg"] } }
    const errorsField = obj.errors;
    if (errorsField && typeof errorsField === "object" && !Array.isArray(errorsField)) {
      const joined = flattenValidationErrors(errorsField as Record<string, unknown>);
      if (isNonEmpty(joined)) return joined;
    }
    // ApiResponse envelope: { errors: string[] }
    if (Array.isArray(errorsField)) {
      const joined = (errorsField as unknown[]).filter(isNonEmpty).join("; ");
      if (isNonEmpty(joined)) return joined;
    }

    // ASP.NET ProblemDetails: { detail, title }
    if (isNonEmpty(obj.detail)) return obj.detail;
    if (isNonEmpty(obj.title)) return obj.title;
    // ApiResponse envelope: { message }
    if (isNonEmpty(obj.message)) return obj.message;
  }

  return FALLBACK_MESSAGE;
}

// Global 401 handler. A client-side route change renders from whatever
// `user` AuthProvider already has in memory (RequireAuth doesn't re-verify
// on navigation), so a session that expired server-side can otherwise leave
// a stale page on screen until the next full reload. AuthProvider registers
// a handler here that redirects to sign-in the moment any request 401s;
// null when no provider is mounted.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

export type QueryParams = object;

type RequestOptions = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown;
  headers?: Record<string, string>;
  query?: QueryParams;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const base = path.startsWith("/") ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      // ASP.NET model binding for `T[]? param` reads repeated query
      // parameters (`?stage=Intake&stage=PreApproval`). Empty arrays
      // are omitted entirely so they don't generate noise.
      for (const item of v) {
        if (item !== undefined && item !== null && item !== "") {
          params.append(k, String(item));
        }
      }
    } else {
      params.append(k, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, query, ...rest } = options;
  const isFormData = body instanceof FormData;

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(isFormData ? {} : body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: isFormData ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? ((await response.json()) as ApiResponse<T>) : null;

  if (!response.ok) {
    if (response.status === 401) onUnauthorized?.();
    // HTTP/2 carries no status text, so `response.statusText` is the empty
    // string on production. Without a fallback, ApiError ends up with an
    // empty message + `errors: [""]`, which renders as a red toast with no
    // body. Always seed at least one non-empty entry.
    const fallback = `Request failed (HTTP ${response.status})`;
    // `payload.errors` is a string[] in the ApiResponse envelope but an
    // OBJECT ({ Field: ["msg"] }) in an ASP.NET ValidationProblemDetails (a
    // model-binding 400 — e.g. a number too large for an int field). Only
    // the array shape is safe to .filter(); guarding it is what stops a
    // model-binding 400 from throwing "errors.filter is not a function",
    // which previously surfaced to the user as that raw TypeError text.
    const rawErrors = payload?.errors;
    const envelopeErrors = Array.isArray(rawErrors) ? rawErrors.filter(isNonEmpty) : [];
    const payloadMessage = payload?.message ?? "";
    // Fall through to extractErrorMessage so a ValidationProblemDetails body
    // yields its field-tagged message instead of just the HTTP status.
    const fromPayload = payload != null ? extractErrorMessage(payload) : FALLBACK_MESSAGE;
    const message = isNonEmpty(payloadMessage)
      ? payloadMessage
      : fromPayload !== FALLBACK_MESSAGE
        ? fromPayload
        : isNonEmpty(response.statusText)
          ? response.statusText
          : fallback;
    const errors = envelopeErrors.length > 0 ? envelopeErrors : [message];
    throw new ApiError(message, response.status, errors, payload?.details);
  }

  // A 2xx that isn't JSON is never a valid API result — it's a masquerade
  // (e.g. a misrouted /api path served the SPA index.html fallback, or a
  // proxy error page). Returning undefined here surfaces as TanStack's
  // opaque "Query data cannot be undefined"; throw a diagnosable error
  // instead. Genuinely empty bodies (204 / bare Ok()) stay valid void
  // results.
  if (!isJson) {
    const text = await response.text();
    if (isNonEmpty(text)) {
      const message = `Expected a JSON response but received "${
        contentType || "no content type"
      }" (HTTP ${response.status})`;
      throw new ApiError(message, response.status, [message]);
    }
    return undefined as T;
  }

  if (payload && "data" in payload) {
    return payload.data as T;
  }
  return undefined as T;
}

/** Like {@link request} but returns the raw response body as a Blob (e.g. a
 *  generated PDF). On a non-2xx it still tries to surface the JSON envelope's
 *  message so callers get a useful error toast. */
async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const { body, headers, query, ...rest } = options;
  const response = await fetch(buildUrl(path, query), {
    ...rest,
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    if (response.status === 401) onUnauthorized?.();
    let message = `Request failed (HTTP ${response.status})`;
    try {
      const payload = (await response.json()) as ApiResponse<unknown>;
      const joined = payload?.errors?.filter(isNonEmpty).join("; ") ?? "";
      if (isNonEmpty(payload?.message)) message = payload!.message!;
      else if (isNonEmpty(joined)) message = joined;
    } catch {
      /* non-JSON error body — keep the HTTP fallback */
    }
    throw new ApiError(message, response.status, [message]);
  }
  return response.blob();
}

export const api = {
  get: <T>(path: string, query?: QueryParams) =>
    request<T>(path, { method: "GET", query }),
  /** GET returning a binary Blob (e.g. a generated/native PDF download). */
  getBlob: (path: string, query?: QueryParams) =>
    requestBlob(path, { method: "GET", query }),
  /** POST returning a binary Blob (PDF preview, etc.). */
  postBlob: (path: string, body?: unknown) =>
    requestBlob(path, { method: "POST", body }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body }),
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form }),
};
